import React, { useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Divider,
  Stack,
} from "@mui/material";
import { Download, Close } from "@mui/icons-material";
import { CreateGraphFromLogsResponse } from "../services/transitionGraphService";

interface TransitionGraphResultDialogProps {
  open: boolean;
  onClose: () => void;
  result: CreateGraphFromLogsResponse | null;
}

export const TransitionGraphResultDialog: React.FC<
  TransitionGraphResultDialogProps
> = ({ open, onClose, result }) => {
  // Markdownファイルとしてダウンロード
  const handleDownload = useCallback(() => {
    if (!result) {
      return;
    }

    // ファイル名を生成（タイムスタンプ付き）
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5);
    const filename = `transition_graph_${timestamp}.md`;

    // Markdownコンテンツを構築（Mermaidグラフのみ）
    const markdownContent = result.graph_text;

    // Blobを作成してダウンロード
    const blob = new Blob([markdownContent], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [result]);

  if (!result) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: "500px" },
      }}
    >
      <DialogTitle>
        遷移情報Graph生成結果
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* 統計情報 */}
          <Paper sx={{ p: 2, bgcolor: "background.default" }}>
            <Typography variant="h6" gutterBottom>
              統計情報
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2">
                処理したログファイル数: <strong>{result.statistics.total_logs_processed}</strong>
              </Typography>
              <Typography variant="body2">
                検出した遷移数: <strong>{result.statistics.total_transitions_found}</strong>
              </Typography>
              <Typography variant="body2">
                識別したビュー数: <strong>{result.statistics.views_identified}</strong>
              </Typography>
            </Stack>
          </Paper>

          <Divider />

          {/* Graph表示 */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Graph
            </Typography>
            <Paper
              sx={{
                p: 2,
                bgcolor: "grey.50",
                overflow: "auto",
                maxHeight: "400px",
              }}
            >
              <Box
                component="pre"
                sx={{
                  margin: 0,
                  fontFamily: "monospace",
                  fontSize: "0.875rem",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {result.graph_text}
              </Box>
            </Paper>
          </Box>

          {/* メッセージ */}
          {result.message && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                {result.message}
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<Close />}>
          閉じる
        </Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          startIcon={<Download />}
        >
          Markdownファイルとしてダウンロード
        </Button>
      </DialogActions>
    </Dialog>
  );
};

